/**
 * Distance calculation utilities for itinerary waypoints
 * Uses route geometry when available for accurate driving distance,
 * falls back to Haversine formula for straight-line distance
 */

import { calculateDistance } from '@/modules/routes/utils/routeCalculations'
import { WaypointResponse } from '@/shared/api/routes'
import { TripRouteScheduleRoute } from '@/shared/api/routes'

export interface WaypointDistance {
  distanceMeters: number
  distanceMiles: number
  distanceFormatted: string
  source: 'route_geometry' | 'haversine'
  durationSeconds?: number // Actual duration from Mapbox route data
}

/**
 * Calculate distance from route geometry (LineString)
 * Extracts segment distance between two waypoints from the geometry
 */
function calculateDistanceFromGeometry(
  geometry: any,
  waypoint1: WaypointResponse,
  waypoint2: WaypointResponse
): number | null {
  if (!geometry || !geometry.coordinates || !Array.isArray(geometry.coordinates)) {
    return null
  }

  if (!waypoint1.latitude || !waypoint1.longitude || !waypoint2.latitude || !waypoint2.longitude) {
    return null
  }

  const coordinates = geometry.coordinates as [number, number][]
  if (coordinates.length < 2) {
    return null
  }

  // Find the closest points in the geometry to each waypoint
  let closestIndex1 = 0
  let closestIndex2 = coordinates.length - 1
  let minDist1 = Infinity
  let minDist2 = Infinity

  for (let i = 0; i < coordinates.length; i++) {
    const [lng, lat] = coordinates[i]
    
    // Distance to waypoint1
    const dist1 = calculateDistance(
      [waypoint1.longitude, waypoint1.latitude],
      [lng, lat]
    )
    if (dist1 < minDist1) {
      minDist1 = dist1
      closestIndex1 = i
    }

    // Distance to waypoint2
    const dist2 = calculateDistance(
      [waypoint2.longitude, waypoint2.latitude],
      [lng, lat]
    )
    if (dist2 < minDist2) {
      minDist2 = dist2
      closestIndex2 = i
    }
  }

  // Calculate distance along the geometry between the two closest points
  if (closestIndex1 >= closestIndex2) {
    // Waypoints are in reverse order or same point
    return null
  }

  let segmentDistance = 0
  for (let i = closestIndex1; i < closestIndex2; i++) {
    const [lng1, lat1] = coordinates[i]
    const [lng2, lat2] = coordinates[i + 1]
    segmentDistance += calculateDistance([lng1, lat1], [lng2, lat2])
  }

  return segmentDistance
}

/**
 * Calculate distance between two waypoints
 * Uses route geometry if available, otherwise Haversine formula
 */
export function calculateWaypointDistance(
  waypoint1: WaypointResponse,
  waypoint2: WaypointResponse,
  routeSchedule?: TripRouteScheduleRoute[]
): WaypointDistance {
  if (!waypoint1.latitude || !waypoint1.longitude || !waypoint2.latitude || !waypoint2.longitude) {
    return {
      distanceMeters: 0,
      distanceMiles: 0,
      distanceFormatted: '0 mi',
      source: 'haversine'
    }
  }

  // Try to find route geometry that contains both waypoints
  if (routeSchedule && routeSchedule.length > 0) {
    for (const routeEntry of routeSchedule) {
      const route = routeEntry.route
      if (!route.geometry) continue

      // Check if both waypoints are in this route's waypoints
      const waypointIds = new Set(routeEntry.waypoints.map(wp => wp.id))
      if (waypointIds.has(waypoint1.id) && waypointIds.has(waypoint2.id)) {
        const geometryDistance = calculateDistanceFromGeometry(
          route.geometry,
          waypoint1,
          waypoint2
        )

        if (geometryDistance !== null && geometryDistance > 0) {
          const miles = geometryDistance * 0.000621371
          // Use actual route duration if available
          const durationSeconds = (route as any).duration_seconds || (route as any).duration
          return {
            distanceMeters: geometryDistance,
            distanceMiles: miles,
            distanceFormatted: miles >= 0.1 ? `${miles.toFixed(1)} mi` : `${Math.round(geometryDistance * 3.28084).toLocaleString()} ft`,
            source: 'route_geometry',
            durationSeconds: durationSeconds
          }
        }
      }
    }
  }

  // Fallback to Haversine formula
  const haversineDistance = calculateDistance(
    [waypoint1.longitude, waypoint1.latitude],
    [waypoint2.longitude, waypoint2.latitude]
  )

  const miles = haversineDistance * 0.000621371
  return {
    distanceMeters: haversineDistance,
    distanceMiles: miles,
    distanceFormatted: miles >= 0.1 ? `${miles.toFixed(1)} mi` : `${Math.round(haversineDistance * 3.28084).toLocaleString()} ft`,
    source: 'haversine'
  }
}

/**
 * Calculate minimum travel time in minutes based on distance
 * Uses average speed of 50 km/h (31 mph) as fallback
 * Prefers actual route duration from Mapbox if available
 */
export function calculateMinimumTravelTime(
  distanceMeters: number, 
  averageSpeedKmh: number = 50,
  routeDurationSeconds?: number
): number {
  if (distanceMeters <= 0) return 0

  // If we have actual route duration from Mapbox, use it
  if (routeDurationSeconds && routeDurationSeconds > 0) {
    return Math.ceil(routeDurationSeconds / 60)
  }

  // Fallback to estimated calculation
  // Convert speed to m/s
  const speedMs = (averageSpeedKmh * 1000) / 3600
  // Calculate time in seconds
  const timeSeconds = distanceMeters / speedMs
  // Convert to minutes
  return Math.ceil(timeSeconds / 60)
}

/**
 * Format travel time in minutes to human-readable string
 */
export function formatTravelTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) {
    return `${hours}h`
  }
  return `${hours}h ${mins}m`
}

